import { zipObject } from "lodash";
import {
  AlertDescriptionClassificationChange,
  AlertDescriptionSampleDownload,
  AlertDescriptionYaraMatch,
  AlertDescriptionThreatIntelligence,
  AlertDescriptionStaticAnalysis,
  AlertDescriptionCloudSandbox,
  AlertDescriptionIntegration
} from "./description-variants";

import { alertfeed } from "../../../shim/jsbridge";
import { IAlert } from "../@types";
import { AlertType } from "../../alertsconfiguration/@types";

type AlertDescriptionProps = {
  data: IAlert;
  linksReloadDocument?: boolean;
  onLinkClick?: () => void;
};

const AlertDescription = ({
  data,
  linksReloadDocument = false,
  onLinkClick
}: AlertDescriptionProps) => {
  const { ALERT_TYPE } = alertfeed;
  const { alert_type: alertType } = data || {};
  const types = zipObject(Object.keys(ALERT_TYPE), Object.keys(ALERT_TYPE));

  const getAlertDescriptionComponent = (type: AlertType) => {
    const renderComponent = () => {
      switch (type) {
        case types.static_analysis_started:
        case types.static_analysis_finished:
        case types.static_analysis_failed:
          return (
            <AlertDescriptionStaticAnalysis
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );
        case types.ticloud_analysis_complete:
        case types.ticloud_upload_complete:
          return (
            <AlertDescriptionThreatIntelligence
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );
        case types.rlcs_analysis_started:
        case types.rlcs_analysis_finished:
        case types.rlcs_analysis_failed:
          return (
            <AlertDescriptionCloudSandbox
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.auxiliary_analysis_started:
        case types.auxiliary_analysis_finished:
        case types.auxiliary_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="Auxiliary"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.joe_analysis_started:
        case types.joe_analysis_finished:
        case types.joe_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="Joe Sandbox"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.cuckoo_analysis_started:
        case types.cuckoo_analysis_finished:
        case types.cuckoo_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="Cuckoo"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.cape_analysis_started:
        case types.cape_analysis_finished:
        case types.cape_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="Cape"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.cisco_sma_analysis_started:
        case types.cisco_sma_analysis_finished:
        case types.cisco_sma_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="Cisco Secure Malware Analytics"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.vmray_analysis_started:
        case types.vmray_analysis_finished:
        case types.vmray_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="VMRay"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.fireeye_analysis_started:
        case types.fireeye_analysis_finished:
        case types.fireeye_analysis_failed:
          return (
            <AlertDescriptionIntegration
              integrationName="FireEye"
              type={type}
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );

        case types.yara_match:
          return (
            <AlertDescriptionYaraMatch
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );
        case types.sample_available:
          return (
            <AlertDescriptionSampleDownload
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );
        case types.classification_change:
          return (
            <AlertDescriptionClassificationChange
              data={data}
              linksReloadDocument={linksReloadDocument}
              onLinkClick={onLinkClick}
            />
          );
        default:
          return <>N/A</>;
      }
    };

    return (
      <span data-testid={`alert_description_${alertType ?? "none"}`}>
        {renderComponent()}
      </span>
    );
  };

  return <>{getAlertDescriptionComponent(alertType as AlertType)}</>;
};

export default AlertDescription;
